import config from '@/config';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';
import NumberUtils from '@/utils/number.utils';

const FALLBACK_BASE_URL = 'https://suarte.art';
const IMAGE_SERVICE_DOMAIN = config.app?.imageServiceDomain ?? 'https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA';

const isAbsoluteUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const sanitizeOrigin = (origin, options = {}) => {
  const { allowLocalhost = false } = options;
  if (typeof origin !== 'string' || !origin) {
    return null;
  }

  const trimmed = origin.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  if (!allowLocalhost && /localhost|127\.0\.0\.1/i.test(trimmed)) {
    return null;
  }

  return trimmed.replace(/\/$/, '');
};

const sanitizeSharerUsername = (username) => {
  if (typeof username !== 'string') {
    return null;
  }
  const normalized = username.trim().replace(/^@+/, '');
  return normalized || null;
};

export function deriveArtistName(artworkData) {
  const artistName = getArtworkArtistDisplayName(artworkData);
  return artistName === '-' ? null : artistName;
}

export function buildArtworkImageUrl(artworkData) {
  const media = artworkData?.artwork_media?.artwork_main_picture
    || artworkData?.artwork_about?.artwork_main_picture
    || null;

  if (!media || typeof media !== 'object') {
    return null;
  }

  const { image_url, url, public_url, secure_url, image_id } = media;
  const candidateUrls = [image_url, url, public_url, secure_url].filter(Boolean);
  for (const candidate of candidateUrls) {
    if (isAbsoluteUrl(candidate)) {
      return candidate;
    }
  }

  if (!image_id) {
    return null;
  }

  const sanitizedId = String(image_id).replace(/^\/+/, '');
  return `${IMAGE_SERVICE_DOMAIN}/${sanitizedId}/public`;
}

function formatAuctionDate(endsAt) {
  if (!endsAt) {
    return null;
  }
  const parsed = new Date(endsAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function deriveSharerUsername(artworkData, explicitSharerUsername) {
  const explicit = sanitizeSharerUsername(explicitSharerUsername);
  if (explicit) {
    return explicit;
  }
  const fromGallery = sanitizeSharerUsername(
    artworkData?.artwork_about?.artwork_gallery?.user_username,
  );
  return fromGallery;
}

export function buildArtworkShareUrl({
  artworkData,
  auctionData,
  baseUrlOverride,
  sharerUsername,
} = {}) {
  const slug = artworkData?.slug
    || artworkData?.artwork_about?.artwork_slug
    || artworkData?._id
    || null;

  const runtimeOrigin = typeof window !== 'undefined' ? sanitizeOrigin(window.location?.origin) : null;
  const overrideOrigin = sanitizeOrigin(baseUrlOverride);
  const baseUrl = overrideOrigin || runtimeOrigin || FALLBACK_BASE_URL;
  if (!slug) {
    return baseUrl;
  }

  const url = new URL(`/artwork/${slug}`, baseUrl);
  const isAuctionArtwork = Boolean(
    artworkData?.artwork_about?.artwork_is_auction
    || auctionData,
  );
  if (isAuctionArtwork) {
    url.searchParams.set('view', 'auction');
  }

  const auctionId = auctionData?._id || auctionData?.id || null;
  if (auctionId) {
    url.searchParams.set('auction', auctionId);
  }

  const normalizedSharer = sanitizeSharerUsername(sharerUsername);
  if (normalizedSharer) {
    url.searchParams.set('shared_by', normalizedSharer);
  }

  url.searchParams.set('ref', 'artwork-share');
  return url.toString();
}

export function resolveArtworkShareMetadata({
  artworkData,
  auctionData,
  baseUrlOverride,
  sharerUsername,
} = {}) {
  const resolvedArtwork = artworkData ?? {};
  const resolvedAuction = auctionData ?? resolvedArtwork?.artwork_auction ?? {};
  const artworkAbout = resolvedArtwork?.artwork_about ?? {};

  const artworkTitle = artworkAbout.artwork_title
    || resolvedArtwork?.title
    || resolvedArtwork?.artwork_title
    || 'Untitled artwork';
  const artistName = deriveArtistName(resolvedArtwork);

  const normalizedSharer = deriveSharerUsername(resolvedArtwork, sharerUsername);
  const shareHeadline = normalizedSharer
    ? `Take a look, @${normalizedSharer} has shared an artwork with you`
    : 'Take a look, an artwork has been shared with you';

  const formattedEndDate = formatAuctionDate(resolvedAuction?.ends_at || resolvedAuction?.end_at);
  const currency = artworkAbout.artwork_currency || resolvedAuction?.currency || 'EUR';
  const currentBidRaw = resolvedAuction?.current_bid_amount
    || resolvedAuction?.current_bid?.amount
    || resolvedAuction?.highest_bid_amount
    || resolvedAuction?.price_starting_at
    || artworkAbout.price_starting_at
    || artworkAbout.artwork_price
    || 0;
  const formattedCurrentBid = Number(currentBidRaw) > 0
    ? NumberUtils.getArtworkPrice(Number(currentBidRaw), currency)
    : null;

  const previewDetails = [];
  if (artistName) {
    previewDetails.push(`By ${artistName}`);
  }
  if (formattedCurrentBid) {
    previewDetails.push(`Current bid ${formattedCurrentBid}`);
  }
  if (formattedEndDate) {
    previewDetails.push(`Ends ${formattedEndDate}`);
  }

  const titleSegment = artistName
    ? `"${artworkTitle}" by ${artistName}`
    : `"${artworkTitle}"`;
  const shareDescription = `${titleSegment} on Suarte.`;
  const shareSummary = previewDetails.length > 0
    ? previewDetails.join(' | ')
    : `Discover ${titleSegment} on Suarte.`;

  return {
    artworkTitle,
    artistName,
    formattedCurrentBid,
    formattedEndDate,
    previewDetails,
    shareSummary,
    shareTitle: shareHeadline,
    shareDescription,
    shareUrl: buildArtworkShareUrl({
      artworkData: resolvedArtwork,
      auctionData: resolvedAuction,
      baseUrlOverride,
      sharerUsername: normalizedSharer,
    }),
    shareImageUrl: buildArtworkImageUrl(resolvedArtwork),
    sharerUsername: normalizedSharer,
  };
}

export function buildArtworkShareGatewayUrl({
  artworkData,
  auctionData,
  apiBaseUrl,
  sharerUsername,
} = {}) {
  const slug = artworkData?.slug
    || artworkData?.artwork_about?.artwork_slug
    || artworkData?._id
    || null;
  const normalizedApiBaseUrl = sanitizeOrigin(apiBaseUrl, { allowLocalhost: true });
  if (!slug || !normalizedApiBaseUrl) {
    return null;
  }

  const url = new URL(`/share/artwork/${slug}`, normalizedApiBaseUrl);
  const isAuctionArtwork = Boolean(
    artworkData?.artwork_about?.artwork_is_auction
    || auctionData,
  );
  if (isAuctionArtwork) {
    url.searchParams.set('view', 'auction');
  }

  const auctionId = auctionData?._id || auctionData?.id || null;
  if (auctionId) {
    url.searchParams.set('auction', auctionId);
  }

  const normalizedSharer = sanitizeSharerUsername(sharerUsername);
  if (normalizedSharer) {
    url.searchParams.set('shared_by', normalizedSharer);
  }
  url.searchParams.set('ref', 'artwork-share');

  return url.toString();
}
